// Clock commands: clock in / out, switch project. Pure; ONE group each (the phone used to write
// clock-in as four separate docs). ctx adds: projects, departments, projectTypes (for the session's
// department stamp and the setup checklist), sessions (clockSessions), clock (meta/clock or null).
import { makeTimerState, computeCardActiveTime } from '../timer.js';
import { localToday, localDateStr } from '../time.js';
import { timeEditRecord } from '../timeledger.js';
import { sessionDepartmentStamp, bucketDepartmentId } from '../workdept.js';
import { planBucketTasks, typeOf } from '../projecttypes.js';
import { automationRunsBucket } from '../processes.js';
import { makeTaskDoc } from '../taskdoc.js';
import { del, mergeUpdates, set, update } from './writes.js';
import { buildTimeEntry, stopTask } from './tasks.js';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const MIN_SESSION_MS = 2000;   // clocked in and right back out = churn, not a session
const MIN_ORPHAN_MS = 60000;   // an orphan with nothing to go on closes as a minute, never as the days it sat open

/**
 * Sessions left open that aren't the running clock (a second clock-in, a stale device's clock-out: the earlier session
 * never ended). `keep` = ids that are live. `ctx.ownerUid` keeps a teammate's clock out of it where ctx.sessions holds the whole org (the MCP). Without a start there's nothing to close.
 */
export function orphanSessions(ctx, keep = []) {
  const live = new Set([ctx.clock?.active ? ctx.clock.sessionId : null, ctx.clock?.active ? ctx.clock.travelSessionId : null, ...keep].filter(Boolean));
  return (ctx.sessions || []).filter((s) => s && !s.endedAt && Number.isFinite(s.startedAt) && !live.has(s.id) && !(ctx.ownerUid && s.ownerUid && s.ownerUid !== ctx.ownerUid));
}

/**
 * When an orphan really stopped, as near as the record says. A drive: its last task segment. Work: the next work
 * session that day; started today with none after it, now (this clock-in or clock-out is what replaced it); else its
 * banked focus time. Never less than a minute, never past `at`.
 */
export function orphanEnd(s, sessions, at) {
  const day = localDateStr(s.startedAt);
  let end;
  if (s.type === 'travel') {
    end = Math.max(0, ...(s.taskSegments || []).map((g) => g.endedAt || g.startedAt || 0));
  } else {
    const next = (sessions || []).filter((x) => x.id !== s.id && x.type !== 'travel' && !x.overlay && x.startedAt > s.startedAt && localDateStr(x.startedAt) === day).map((x) => x.startedAt);
    if (next.length) end = Math.min(...next);
    else if (day === localDateStr(at)) end = at;
    else end = s.startedAt + (s.focusedTime || 0);
  }
  return Math.min(at, Math.max(end, s.startedAt + MIN_ORPHAN_MS));
}

function closeOrphanWrites(ctx, keep, at) {
  return orphanSessions(ctx, keep).map((s) => {
    const end = orphanEnd(s, ctx.sessions, at);
    return update(`clockSessions/${s.id}`, { endedAt: end, duration: end - s.startedAt, endedBy: 'orphan', updatedAt: at });
  });
}

/** Close every orphan in one group. → { writes, closed: [{ id, type, projectId, startedAt, endedAt }] } */
export function closeOrphans(ctx) {
  const at = now(ctx);
  const closed = orphanSessions(ctx).map((s) => ({ id: s.id, type: s.type || 'work', projectId: s.projectId || null, startedAt: s.startedAt, endedAt: orphanEnd(s, ctx.sessions, at) }));
  return { writes: closeOrphanWrites(ctx, [], at), closed };
}

/**
 * Work sessions carry their project's department; feedBooks:false when that department's time must not reach
 * BattleBooks. Department mode (ctx.departmentMode, D5): `perTask` instead — each time entry carries its task's
 * department and the books / reports take it from there (workdept.js).
 */
export function sessionStamp(ctx, projectId, taskId = null) {
  if (typeof ctx.stampFor === 'function') return ctx.stampFor(projectId, taskId);
  return sessionDepartmentStamp(ctx, projectId, taskId);
}

/** Today's setup / cleanup checklist for a project: create the missing items, carry unticked ones forward. */
export function bucketWrites(ctx, { projectId, bucket }) {
  const project = (ctx.projects || []).find((p) => p.id === projectId);
  if (!project || !automationRunsBucket(project, bucket)) return { writes: [], created: [] };   // the project can turn its checklist off (automations)
  const date = localToday();
  const type = typeof ctx.typeFor === 'function' ? ctx.typeFor(project) : typeOf(project, ctx.projectTypes || []);
  const { create, redate } = planBucketTasks({ project, type, bucket, date, tasks: ctx.tasks || [] });
  const at = now(ctx);
  const dept = ctx.departmentMode ? bucketDepartmentId(project, type) : null;   // D5: they carry a department (else General)
  const created = create.map((t, i) => makeTaskDoc(dept ? { ...t, departmentId: dept } : t, [], at + i, ctx.newId));
  const writes = [...created.map((t) => set(`tasks/${t.id}`, t)), ...redate.map((id) => update(`tasks/${id}`, { bucketDate: date, scheduledDate: date }))];
  return { writes, created };
}

/**
 * Re-file a work session onto another project (clocked to the wrong one). Carries the new project's
 * department stamp; a live session moves the clock with it. Drives are not work sessions.
 */
export function moveSession(ctx, { sessionId, projectId = null }) {
  const s = (ctx.sessions || []).find((x) => x.id === sessionId);
  if (!s) throw new Error(`no clock session ${sessionId}`);
  if (s.type === 'travel') throw new Error('that is a drive, not a work session');
  const pid = projectId ? String(projectId) : null;
  if (pid && !(ctx.projects || []).some((p) => p.id === pid)) throw new Error(`no project ${pid}`);
  if ((s.projectId || null) === pid) throw new Error('the session is already on that project');
  const st = sessionStamp(ctx, pid, s.currentTaskId || null);
  const writes = [update(`clockSessions/${sessionId}`, { projectId: pid, departmentId: st.departmentId, feedBooks: st.feedBooks === false ? false : null, updatedAt: now(ctx) })];
  if (ctx.clock?.active && ctx.clock.sessionId === sessionId) writes.push(update('meta/clock', { projectId: pid }));
  return { writes, session: s, from: s.projectId || null, to: pid };
}

const hhmm = (ms) => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const sessionSlice = (s) => { const end = s.endedAt || s.startedAt; return { id: s.id, date: localDateStr(s.startedAt), startTime: hhmm(s.startedAt), endTime: hhmm(end), duration: end - s.startedAt }; };

/**
 * Fix a finished session's clock times — forgot to clock out, clocked in late. Refuses the running session
 * (clock out first; an orphan left open can be closed here, given an end), an end before the start, more than 24 h, or an end in the future. The change is kept in the
 * time ledger (kind 'session') in the same group, so the hours BattleBooks gets and the history agree.
 */
export function editSession(ctx, { sessionId, startedAt, endedAt, reason = null }) {
  const s = (ctx.sessions || []).find((x) => x.id === sessionId);
  if (!s) throw new Error(`no clock session ${sessionId}`);
  const orphan = !s.endedAt && orphanSessions(ctx).some((x) => x.id === s.id);
  if (!s.endedAt && !orphan) throw new Error('that session is still running — clock out first, then fix its times');
  if (orphan && !Number.isFinite(endedAt)) throw new Error('that session was left open — give it an end time');
  const a = Number.isFinite(startedAt) ? startedAt : s.startedAt;
  const b = Number.isFinite(endedAt) ? endedAt : s.endedAt;
  if (!(b > a)) throw new Error('the end must be after the start');
  if (b - a > 24 * 3600000) throw new Error('a session can’t be longer than 24 hours');
  if (b > now(ctx) + 60000) throw new Error('a session can’t end in the future');
  if (a === s.startedAt && b === s.endedAt) throw new Error('nothing changed');
  const at = now(ctx);
  const writes = [update(`clockSessions/${sessionId}`, { startedAt: a, endedAt: b, duration: b - a, date: localDateStr(a), editedAt: at, updatedAt: at })];
  const project = (ctx.projects || []).find((p) => p.id === s.projectId) || null;
  const id = ctx.newId();
  const record = timeEditRecord({
    id, kind: 'session', task: { id: null, projectId: s.projectId || null, title: project ? `${project.name} (clock)` : 'Clock session' },
    before: sessionSlice(s), after: sessionSlice({ id: s.id, startedAt: a, endedAt: b }), by: ctx.by || null, reason, now: at,
  });
  writes.push(set(`timeEdits/${id}`, { ...record, sessionId }));
  return { writes, record };
}

/**
 * Split a finished work session into slices on other projects (Lj, Nov 3: "fix the oct 31 items" — one morning clocked
 * to one job held a dump run for another, a battery run for a third and lunch). `slices` = [{ startedAt, endedAt,
 * projectId }] inside the session, not overlapping. The parts between slices stay on the session's own project. The
 * original doc keeps the first part; the rest are new sessions. One group, kept in the time ledger.
 */
export function splitSession(ctx, { sessionId, slices = [], reason = null }) {
  const s = (ctx.sessions || []).find((x) => x.id === sessionId);
  if (!s) throw new Error(`no clock session ${sessionId}`);
  if (s.type === 'travel') throw new Error('that is a drive, not a work session');
  if (!s.endedAt) throw new Error('that session is still running — clock out first, then split it');
  const cuts = [...slices].map((x) => ({ startedAt: Number(x.startedAt), endedAt: Number(x.endedAt), projectId: x.projectId ? String(x.projectId) : null }))
    .sort((a, b) => a.startedAt - b.startedAt);
  if (!cuts.length) throw new Error('nothing to split');
  for (const [i, c] of cuts.entries()) {
    if (!(c.endedAt > c.startedAt)) throw new Error('each slice must end after it starts');
    if (c.startedAt < s.startedAt || c.endedAt > s.endedAt) throw new Error('every slice must sit inside the session');
    if (i && c.startedAt < cuts[i - 1].endedAt) throw new Error('slices overlap');
    if (c.projectId && !(ctx.projects || []).some((p) => p.id === c.projectId)) throw new Error(`no project ${c.projectId}`);
  }
  // the whole span as parts: the session's own project between slices, each slice on its project
  const parts = [];
  let at = s.startedAt;
  for (const c of cuts) {
    if (c.startedAt - at >= MIN_SESSION_MS) parts.push({ startedAt: at, endedAt: c.startedAt, projectId: s.projectId || null });
    parts.push(c);
    at = c.endedAt;
  }
  if (s.endedAt - at >= MIN_SESSION_MS) parts.push({ startedAt: at, endedAt: s.endedAt, projectId: s.projectId || null });
  const t = now(ctx);
  const span = s.endedAt - s.startedAt;
  const focus = (p) => (num(s.focusedTime) > 0 && span > 0 ? Math.round((num(s.focusedTime) * (p.endedAt - p.startedAt)) / span) : 0);
  const writes = [];
  const out = parts.map((p, i) => {
    const st = sessionStamp(ctx, p.projectId, null);
    const doc = {
      startedAt: p.startedAt, endedAt: p.endedAt, duration: p.endedAt - p.startedAt, date: localDateStr(p.startedAt), projectId: p.projectId,
      departmentId: st.departmentId, feedBooks: st.feedBooks === false ? false : null, focusedTime: focus(p), editedAt: t, updatedAt: t,
    };
    if (i === 0) { writes.push(update(`clockSessions/${s.id}`, doc)); return { id: s.id, ...doc }; }
    const id = ctx.newId();
    const full = { id, type: 'work', orgId: s.orgId || null, ownerUid: s.ownerUid || null, perTask: s.perTask ?? true, currentTaskId: null, currentTaskAt: null, currentPause: null, splitFrom: s.id, createdAt: t, ...doc };
    writes.push(set(`clockSessions/${id}`, full));
    return full;
  });
  const project = (ctx.projects || []).find((p) => p.id === s.projectId) || null;
  const id = ctx.newId();
  const record = timeEditRecord({
    id, kind: 'session', task: { id: null, projectId: s.projectId || null, title: project ? `${project.name} (clock, split)` : 'Clock session (split)' },
    before: sessionSlice(s), after: sessionSlice(out[0]), by: ctx.by || null, reason, now: t,
  });
  writes.push(set(`timeEdits/${id}`, { ...record, sessionId: s.id, parts: out.map((p) => ({ id: p.id, startedAt: p.startedAt, endedAt: p.endedAt, projectId: p.projectId })) }));
  return { writes, parts: out, record };
}

/**
 * Add a finished work session after the fact (Lj, Nov 3: "i'd like to track as much as possible" — a coffee stop before
 * the clock went on). Refuses an end before the start, more than 24 h, the future, or overlap with another work session
 * of the same person. Kept in the time ledger.
 */
export function addSession(ctx, { projectId = null, startedAt, endedAt, reason = null }) {
  const a = Number(startedAt); const b = Number(endedAt);
  if (!Number.isFinite(a) || !Number.isFinite(b) || !(b > a)) throw new Error('the end must be after the start');
  if (b - a > 24 * 3600000) throw new Error('a session can’t be longer than 24 hours');
  if (b > now(ctx) + 60000) throw new Error('a session can’t end in the future');
  const pid = projectId ? String(projectId) : null;
  if (pid && !(ctx.projects || []).some((p) => p.id === pid)) throw new Error(`no project ${pid}`);
  const owner = ctx.ownerUid || ctx.by?.uid || null;
  const clash = (ctx.sessions || []).find((s) => s && s.type !== 'travel' && !s.deletedAt && (!owner || !s.ownerUid || s.ownerUid === owner)
    && s.startedAt < b && (s.endedAt || now(ctx)) > a);
  if (clash) throw new Error(`that overlaps the ${localDateStr(clash.startedAt)} session ${hhmm(clash.startedAt)}–${clash.endedAt ? hhmm(clash.endedAt) : 'now'}`);
  const t = now(ctx);
  const st = sessionStamp(ctx, pid, null);
  const id = ctx.newId();
  const doc = {
    id, type: 'work', projectId: pid, departmentId: st.departmentId, feedBooks: st.feedBooks === false ? false : null, ownerUid: owner,
    startedAt: a, endedAt: b, duration: b - a, date: localDateStr(a), focusedTime: 0, perTask: true, currentTaskId: null, currentTaskAt: null, currentPause: null,
    manual: true, createdAt: t, editedAt: t, updatedAt: t,
  };
  const project = (ctx.projects || []).find((p) => p.id === pid) || null;
  const rid = ctx.newId();
  const record = timeEditRecord({ id: rid, kind: 'session', task: { id: null, projectId: pid, title: project ? `${project.name} (clock, added)` : 'Clock session (added)' }, before: null, after: sessionSlice(doc), by: ctx.by || null, reason, now: t });
  return { writes: [set(`clockSessions/${id}`, doc), set(`timeEdits/${rid}`, { ...record, sessionId: id })], session: doc, record };
}

/** Throw a finished drive away (a drive that never happened: no fixes, started by a stale fence). Work sessions are edited, never deleted. */
export function deleteDrive(ctx, { sessionId }) {
  const s = (ctx.sessions || []).find((x) => x.id === sessionId);
  if (!s) throw new Error(`no clock session ${sessionId}`);
  if (s.type !== 'travel') throw new Error('that is a work session, not a drive — edit its times instead');
  if (!s.endedAt && ctx.clock?.active && ctx.clock.travelSessionId === sessionId) throw new Error('that drive is still running — stop it first');
  return { writes: [del(`clockSessions/${sessionId}`)], session: s };
}

/**
 * Clock in (optionally straight onto a task). Refuses when already clocked in — switchProject instead.
 * `startedAt` (ms, earlier today) backdates it — "clock me in from when I started": work sessions that lie wholly
 * inside [startedAt, now] are folded into this one (deleted); one that straddles startedAt is refused.
 */
export function clockIn(ctx, { projectId = null, taskId = null, startedAt = null } = {}) {
  if (ctx.clock?.active) throw new Error(`already clocked in${ctx.clock.projectId ? ` (project ${ctx.clock.projectId})` : ''} — switchProject or clockOut first`);
  if (projectId && !(ctx.projects || []).some((p) => p.id === projectId)) throw new Error(`no project ${projectId}`);
  if (taskId && !(ctx.tasks || []).some((t) => t.id === taskId)) throw new Error(`no task ${taskId}`);
  const folded = [];
  if (startedAt != null) {
    if (!Number.isFinite(startedAt) || startedAt > now(ctx)) throw new Error('the start must be a time before now');
    if (localDateStr(startedAt) !== localToday()) throw new Error('a backdated clock-in must start today');
    for (const s of ctx.sessions || []) {
      if (s.type === 'travel' || !s.endedAt || s.endedAt <= startedAt) continue;
      // "from 8:37" when the last session ended 8:37:20 means right after it
      if (s.startedAt < startedAt && s.endedAt - startedAt < 60000) { startedAt = s.endedAt; continue; }
      if (s.startedAt < startedAt) throw new Error(`a session runs ${hhmm(s.startedAt)}–${hhmm(s.endedAt)} across that start — start at ${hhmm(s.endedAt)} or later`);
      folded.push(s);
    }
  }
  const at = startedAt ?? now(ctx);
  const sessionId = ctx.newId();
  const pid = projectId ? String(projectId) : null;
  const clock = { startedAt: at, projectId: pid, sessionId, mode: 'work', active: true };
  const session = { ...sessionStamp(ctx, pid, taskId), id: sessionId, projectId: pid, type: 'work', startedAt: at, endedAt: null, duration: 0, focusedTime: 0, date: localToday(), currentTaskId: taskId || null, ...(taskId ? { currentTaskAt: at } : {}) };
  const timer = makeTimerState({ startedAt: at, ...(taskId ? { taskId } : {}) });
  const writes = [set('meta/clock', clock), set(`clockSessions/${sessionId}`, session), set('meta/timer', timer), ...folded.map((s) => del(`clockSessions/${s.id}`)), ...closeOrphanWrites(ctx, [sessionId], now(ctx))];
  const bucket = pid ? bucketWrites(ctx, { projectId: pid, bucket: 'setup' }) : { writes: [], created: [] };
  writes.push(...bucket.writes);
  return { writes, clock, session, timer, setupTasks: bucket.created, folded };
}

/**
 * Clock out: bank the live task's time, close the session (drop a <2 s stub), close an open travel
 * overlay, reset clock / timer / lock — together or not at all.
 */
export function clockOut(ctx, { travelSegments = null } = {}) {
  const cl = ctx.clock;
  if (!cl?.active) throw new Error('not clocked in');
  const at = now(ctx);
  const writes = [];
  const timer = ctx.timer;
  const flushTaskId = timer?.taskId ?? (ctx.lock?.active ? ctx.lock.taskId : null) ?? null;
  const elapsed = timer ? computeCardActiveTime(timer, at) : 0;
  const timeEntry = elapsed > 0 ? buildTimeEntry(elapsed, at, ctx.newId) : null;
  let banked = 0;
  if (flushTaskId && elapsed > 0 && (ctx.tasks || []).some((t) => t.id === flushTaskId)) {
    const r = stopTask(ctx, { id: flushTaskId, elapsed, pauseTime: timer?.accumulatedPause || 0, sessionId: cl.sessionId || null, timeEntry, keepTimer: true, keepLock: true });
    writes.push(...r.writes);
    banked = elapsed;
  }
  if (cl.traveling && cl.travelSessionId) {
    const segs = Array.isArray(travelSegments) ? travelSegments.map((s) => (s.endedAt ? s : { ...s, endedAt: at })) : null;
    if ((ctx.sessions || []).some((s) => s.id === cl.travelSessionId)) writes.push(update(`clockSessions/${cl.travelSessionId}`, { endedAt: at, duration: at - (cl.travelStartedAt || at), ...(segs ? { taskSegments: segs } : {}) }));
  }
  const duration = at - cl.startedAt;
  let session = null;
  if (cl.sessionId) {
    const exists = (ctx.sessions || []).some((s) => s.id === cl.sessionId);
    if (duration >= 0 && duration < MIN_SESSION_MS && !timeEntry) { if (exists) writes.push(del(`clockSessions/${cl.sessionId}`)); session = { id: cl.sessionId, dropped: true }; }
    else if (exists) { writes.push(update(`clockSessions/${cl.sessionId}`, { endedAt: at, duration })); session = { id: cl.sessionId, duration }; }
    else { session = { ...sessionStamp(ctx, cl.projectId || null), id: cl.sessionId, projectId: cl.projectId || null, type: 'work', startedAt: cl.startedAt, endedAt: at, duration, focusedTime: banked, date: localToday() }; writes.push(set(`clockSessions/${cl.sessionId}`, session)); }
  } else if (duration >= MIN_SESSION_MS || timeEntry) {
    session = { ...sessionStamp(ctx, cl.projectId || null), id: ctx.newId(), projectId: cl.projectId || null, type: 'work', startedAt: cl.startedAt, endedAt: at, duration, focusedTime: 0, date: localToday() };
    writes.push(set(`clockSessions/${session.id}`, session));
  }
  writes.push(...closeOrphanWrites(ctx, [cl.sessionId, cl.travelSessionId].filter(Boolean), at));
  writes.push(set('meta/clock', { active: false }), set('meta/timer', makeTimerState({ startedAt: at })), set('meta/lock', { active: false, taskId: null }));
  return { writes: mergeUpdates(writes), session, banked, duration };
}

/**
 * Start a drive on the running clock (the travel overlay): one group. For the headless recorder, which has no
 * TaskContext; `at` backdates it to when the truck started moving. Never clocks you in.
 */
export function startTravel(ctx, { at = null } = {}) {
  const cl = ctx.clock;
  if (!cl?.active) throw new Error('not clocked in');
  if (cl.traveling) throw new Error('already traveling');
  const t = now(ctx);
  const start = Number.isFinite(at) ? Math.min(at, t) : t;
  const sessionId = `tr${start}`;
  const taskId = ctx.timer?.taskId || null;
  const task = taskId ? (ctx.tasks || []).find((x) => x.id === taskId) : null;
  const clock = { ...cl, active: true, traveling: true, travelStartedAt: start, travelSessionId: sessionId };
  const session = { id: sessionId, type: 'travel', overlay: true, projectId: null, startedAt: start, endedAt: null, duration: 0, focusedTime: 0, date: localDateStr(start), taskSegments: [{ taskId, projectId: task?.projectId ? String(task.projectId) : (cl.projectId || null), startedAt: start }] };
  return { writes: [set('meta/clock', clock), set(`clockSessions/${sessionId}`, session)], clock, session };
}

/**
 * End the running drive (the travel overlay) without touching the work clock. `at` backdates the end — the
 * stillness watch found the truck parked since then (shared/travel.js); the open task segment closes there too.
 * `drop` throws the drive away instead (a drive the phone never got a fix for is not a drive).
 */
export function stopTravel(ctx, { at = null, travelSegments = null, drop = false } = {}) {
  const cl = ctx.clock;
  if (!cl?.active || !cl.traveling) throw new Error('not traveling');
  const t = now(ctx);
  const end = Number.isFinite(at) ? Math.min(Math.max(at, cl.travelStartedAt || at), t) : t;
  const writes = [];
  const tid = cl.travelSessionId || null;
  const sess = tid ? (ctx.sessions || []).find((s) => s.id === tid) : null;
  const segs = (Array.isArray(travelSegments) ? travelSegments : sess?.taskSegments || [])
    .filter((s) => !Number.isFinite(s.startedAt) || s.startedAt <= end)
    .map((s) => (s.endedAt && s.endedAt <= end ? s : { ...s, endedAt: end }));
  if (sess) {
    if (drop) writes.push(del(`clockSessions/${tid}`));
    else writes.push(update(`clockSessions/${tid}`, { endedAt: end, duration: end - (cl.travelStartedAt || end), taskSegments: segs, ...(Number.isFinite(at) ? { endedBy: 'parked' } : {}) }));
  }
  const { traveling, travelStartedAt, travelSessionId, travelAnchor, travelMovedAt, ...rest } = cl;
  const clock = { ...rest, active: true };
  writes.push(set('meta/clock', clock));
  return { writes, clock, endedAt: end, dropped: !!(drop && sess), sessionId: tid };
}

/** Switch the running clock to another project: bank the live task, close the old session, open a new one. */
export function switchProject(ctx, { projectId, taskId = null }) {
  const cl = ctx.clock;
  if (!cl?.active) throw new Error('not clocked in — clockIn instead');
  const pid = projectId ? String(projectId) : null;
  if (pid && !(ctx.projects || []).some((p) => p.id === pid)) throw new Error(`no project ${pid}`);
  if (pid === (cl.projectId || null)) throw new Error('already on that project');
  const at = now(ctx);
  const writes = [];
  const timer = ctx.timer;
  if (timer?.taskId) {
    const elapsed = computeCardActiveTime(timer, at);
    if (elapsed > 0 && (ctx.tasks || []).some((t) => t.id === timer.taskId)) {
      writes.push(...stopTask(ctx, { id: timer.taskId, elapsed, sessionId: cl.sessionId || null, timeEntry: buildTimeEntry(elapsed, at, ctx.newId), keepTimer: true, keepLock: true }).writes);
    }
  }
  const duration = at - cl.startedAt;
  if (cl.sessionId && (ctx.sessions || []).some((s) => s.id === cl.sessionId)) {
    if (duration < MIN_SESSION_MS) writes.push(del(`clockSessions/${cl.sessionId}`));
    else writes.push(update(`clockSessions/${cl.sessionId}`, { endedAt: at, duration }));
  }
  const sessionId = ctx.newId();
  const session = { ...sessionStamp(ctx, pid, taskId), id: sessionId, projectId: pid, type: 'work', startedAt: at, endedAt: null, duration: 0, focusedTime: 0, date: localToday(), currentTaskId: taskId || null, ...(taskId ? { currentTaskAt: at } : {}) };
  const clock = { ...cl, startedAt: at, projectId: pid, sessionId, active: true };
  writes.push(set(`clockSessions/${sessionId}`, session), set('meta/clock', clock), set('meta/timer', makeTimerState({ startedAt: at, ...(taskId ? { taskId } : {}) })));
  const bucket = pid ? bucketWrites(ctx, { projectId: pid, bucket: 'setup' }) : { writes: [], created: [] };
  writes.push(...bucket.writes);
  return { writes: mergeUpdates(writes), clock, session, setupTasks: bucket.created };
}
